import { Box, Edges, Html, useTexture } from "@react-three/drei"
import { createMissingTexture } from "@/lib/missing-texture"
import * as THREE from "three"
import { Suspense, useEffect, useRef } from "react"
import type { MinecraftColorProperties, MinecraftModel } from "@/types"
import { MinecraftTintableMaterial } from "./minecraft-tintable-material"
import { useLoader } from "@react-three/fiber"

export interface SceneElement {
  type: "cube"
  dimensions: [number, number, number]
  position: [number, number, number]
  faces: Record<
    string,
    { uv?: [number, number, number, number]; texture: string; tintindex?: number; cullface?: string }
  >
  rotation?: { angle: number; axis: "x" | "y" | "z"; origin: [number, number, number] }
}

export interface SceneDescription {
  elements: SceneElement[]
  ambientocclusion?: boolean
}
// ---

let missingTextureInstance: THREE.CanvasTexture | null = null
export function getMissingTextureInstance(): THREE.CanvasTexture {
  if (!missingTextureInstance) {
    missingTextureInstance = createMissingTexture()
  }
  return missingTextureInstance
}

export function normalizeModelIdForUploadedLookup(id: string): string {
  console.log("[NormalizeUploaded] Input:", id)
  let normalized = id
  if (normalized.startsWith("#")) normalized = normalized.substring(1) // For keys like #stone
  const colonIndex = normalized.lastIndexOf(":")
  if (colonIndex !== -1) {
    normalized = normalized.substring(colonIndex + 1)
  }
  const slashIndex = normalized.lastIndexOf("/")
  if (slashIndex !== -1) {
    normalized = normalized.substring(slashIndex + 1)
  }
  console.log("[NormalizeUploaded] Output:", normalized)
  return normalized
}

// Prepares ID for path construction (keeps block/item path)
export function prepareIdForPath(id: string): string {
  console.log("[PreparePath] Input:", id)
  let pathId = id
  pathId = pathId.replace(/.+:/, "")
  if (pathId.startsWith("#")) {
    // For texture variables used as model names (e.g. #stone)
    pathId = pathId.substring(1)
    // Attempt to infer type if it's a simple name, default to block
    if (!pathId.includes("/")) {
      pathId = `block/${pathId}`
    }
  }
  console.log("[PreparePath] Output:", pathId)
  return pathId
}

export async function resolveModelInheritance(
  modelId: string, // The raw ID from structure.keys or parent string
  allUploadedModels: Map<string, MinecraftModel>,
  vanillaModelsCache: Map<string, MinecraftModel | "not_found" | "fetching">,
  maxDepth = 10,
  currentDepth = 0,
  visited: Set<string> = new Set(),
): Promise<MinecraftModel | null> {
  if (currentDepth > maxDepth) {
    console.error(`Max recursion depth reached resolving inheritance for: ${modelId}`)
    return null
  }
  if (visited.has(modelId)) {
    console.error(`Circular dependency detected for: ${modelId}`)
    return null
  }
  visited.add(modelId)

  const isCobblestoneRelated = modelId.toLowerCase().includes("cobblestone")
  const normalizedForUploaded = normalizeModelIdForUploadedLookup(modelId)
  const preparedPathId = prepareIdForPath(modelId) // This is `prepareIdForPath(modelId)`

  if (isCobblestoneRelated) {
    console.log(`[ResolveInheritance] Processing cobblestone-related ID: ${modelId}`)
    console.log(`  Using normalized for uploaded check: '${normalizedForUploaded}'`)
    console.log(`  Using prepared for path/vanilla check: '${preparedPathId}'`)
    console.log(`  Uploaded models has '${normalizedForUploaded}'`, allUploadedModels.has(normalizedForUploaded))
    if (allUploadedModels.has(normalizedForUploaded)) {
      console.log("  Uploaded cobblestone model content:", JSON.stringify(allUploadedModels.get(normalizedForUploaded)))
    }
    console.log(`  Vanilla cache has '${preparedPathId}'`, vanillaModelsCache.has(preparedPathId))
    if (vanillaModelsCache.has(preparedPathId)) {
      const cachedVanilla = vanillaModelsCache.get(preparedPathId)
      console.log(
        "  Vanilla cobblestone cache content:",
        cachedVanilla === "not_found" || cachedVanilla === "fetching" ? cachedVanilla : JSON.stringify(cachedVanilla),
      )
    }
  }

  let model: MinecraftModel | null = null
  // 1. Check uploaded models
  if (allUploadedModels.has(normalizedForUploaded)) {
    model = { ...allUploadedModels.get(normalizedForUploaded)! } // Create a copy
  } else {
    // 2. Check vanilla models cache or fetch
    if (vanillaModelsCache.has(preparedPathId)) {
      const cached = vanillaModelsCache.get(preparedPathId)
      if (cached === "not_found" || cached === "fetching") return null
      model = { ...cached! } // Create a copy
    } else {
      vanillaModelsCache.set(preparedPathId, "fetching")
      try {
        const response = await fetch(`/models/${preparedPathId}.json`)
        if (!response.ok) {
          if (response.status !== 404)
            console.warn(
              `Failed to fetch vanilla model ${modelId} (path: /models/${preparedPathId}.json): ${response.status}`,
            )
          vanillaModelsCache.set(preparedPathId, "not_found")
          return null
        }
        const vanillaJson = await response.json()
        model = vanillaJson as MinecraftModel
        vanillaModelsCache.set(preparedPathId, { ...model }) // Cache a copy
      } catch (err) {
        console.error(`Error fetching or parsing vanilla model ${modelId}:`, err)
        vanillaModelsCache.set(preparedPathId, "not_found")
        return null
      }
    }
  }

  if (!model) {
    // console.warn(`Model ${modelId} (normalized: ${normalizedForUploaded}, pathId: ${prepareIdForPath(modelId)}) not found.`);
    return null
  }

  let effectiveModel: MinecraftModel = model // Already a copy

  // 3. Resolve parent if it exists
  if (model.parent) {
    // Pass a new Set for visited to allow diamond inheritance if paths differ, but still catch cycles on exact path
    const parentContribution = await resolveModelInheritance(
      model.parent,
      allUploadedModels,
      vanillaModelsCache,
      maxDepth,
      currentDepth + 1,
      new Set(visited),
    )

    if (parentContribution) {
      effectiveModel = {
        ...parentContribution,
        ...effectiveModel, // Child (current model) overrides parent
        elements:
          effectiveModel.elements && effectiveModel.elements.length > 0
            ? effectiveModel.elements
            : parentContribution.elements,
        textures: { ...(parentContribution.textures || {}), ...(effectiveModel.textures || {}) },
        display: { ...(parentContribution.display || {}), ...(effectiveModel.display || {}) },
      }
      if (effectiveModel.ambientocclusion === undefined && parentContribution.ambientocclusion !== undefined) {
        effectiveModel.ambientocclusion = parentContribution.ambientocclusion
      }
    } else {
      // console.warn(`Parent model ${model.parent} for ${modelId} could not be resolved.`)
    }
  }

  if (!effectiveModel.elements) {
    effectiveModel.elements = []
  }

  if (isCobblestoneRelated) {
    if (model) {
      // model is the direct model found for modelId
      console.log(
        `[ResolveInheritance] Cobblestone model directly found (NO parent contribution):`,
        JSON.stringify(model),
      )
      console.log(`  Elements count: ${model.elements?.length}`)
    }
    if (effectiveModel) {
      console.log(
        `[ResolveInheritance] Cobblestone EFFECTIVE model (after parent processing, if any) for ID '${modelId}':`,
        JSON.stringify(effectiveModel),
      )
      console.log(`  Effective elements count: ${effectiveModel.elements?.length}`)
      console.log(`  Effective textures: ${JSON.stringify(effectiveModel.textures)}`)
    } else {
      console.warn(
        `[ResolveInheritance] Cobblestone model ultimately NOT resolved for ID: ${modelId}. (Used normalized: '${normalizedForUploaded}', used preparedPath: '${preparedPathId}')`,
      )
    }
  }

  return effectiveModel
}

// Component to render a single Minecraft model element (cube)
export function MinecraftElement({ elementData , isSelected, colorProps }: { elementData: SceneElement, isSelected?: boolean, colorProps?: MinecraftColorProperties }) {
  const { dimensions, position, rotation, faces } = elementData
  const faceNames = ["right", "left", "top", "bottom", "front", "back"]
  const minecraftFaceMap = { east: 0, west: 1, up: 2, down: 3, south: 4, north: 5 }

  // Process textures for each face
  const faceTexturePaths = faceNames.map((_, index) => {
    // Find the corresponding Minecraft face
    const minecraftFaceName = Object.keys(minecraftFaceMap).find(
      (key) => minecraftFaceMap[key as keyof typeof minecraftFaceMap] === index,
    )

    let textureKeyToUse: string | undefined = undefined
    if (minecraftFaceName && faces && faces[minecraftFaceName]) {
      textureKeyToUse = faces[minecraftFaceName].texture
    } else if (faces) {
      if (faces["all"]) textureKeyToUse = faces["all"].texture
      else {
        const firstFaceKey = Object.keys(faces)[0]
        if (firstFaceKey) textureKeyToUse = faces[firstFaceKey].texture
      }
    }
    return processTexturePath(textureKeyToUse)
  })

  // useTexture will attempt to load these. If a path is undefined, it might error or return a placeholder.
  // We provide a known "bad" path for undefined ones to force a consistent behavior from useTexture.
  const texturesFromHook = useTexture(faceTexturePaths.map((path) => path || "/nonexistent-texture-for-fallback.png"))

  const materials: (THREE.MeshStandardMaterial | MinecraftTintableMaterial)[] = texturesFromHook.map((loadedTexture, index) => {
    let finalTexture: THREE.Texture | THREE.CanvasTexture = loadedTexture
    const originalPath = faceTexturePaths[index]

    // If the original path was undefined, or if the loaded texture's source indicates it's the fallback, use missing texture.
    // A more robust check for useTexture failure might be needed if it doesn't use image.src for fallbacks.
    let useMissing = false
    if (!originalPath) {
      useMissing = true
    } else if (loadedTexture && loadedTexture.image instanceof HTMLImageElement) {
      if (loadedTexture.image.src.endsWith("/nonexistent-texture-for-fallback.png")) {
        useMissing = true
      }
      // Check for 0x0 image, often indicates load failure if not the placeholder
      if (loadedTexture.image.naturalWidth === 0 && loadedTexture.image.naturalHeight === 0 && originalPath) {
        useMissing = true
      }
    } else if (!loadedTexture && originalPath) {
      // If useTexture returned undefined for a valid path attempt
      useMissing = true
    }

    if (useMissing) {
      finalTexture = getMissingTextureInstance()
    }

    if (finalTexture) {
      finalTexture.magFilter = THREE.NearestFilter
      finalTexture.minFilter = THREE.NearestFilter
      if (finalTexture.image) finalTexture.needsUpdate = true // Ensure canvas/data textures update
    } else {
      return new THREE.MeshStandardMaterial({ color: 0x333333, side: THREE.FrontSide })
    }
    const colormapTex = useLoader(
      THREE.TextureLoader,
      colorProps?.colormap ? `/textures/colormap/${colorProps.colormap}.png` : "/placeholder.png",
    )

    return new MinecraftTintableMaterial({
      //colormapTexture: colorProps?.colormap ? colormapTex : undefined,
      tintColor: colorProps?.baseTint,
      //useColormap: !!colorProps?.colormap,
      //useTintColor: !!colorProps?.baseTint,
      baseTexture: finalTexture,
      transparent: true, // Keep for alphaTest
      alphaTest: 0.1,
      depthWrite: true, // Explicitly set depthWrite
      side: THREE.FrontSide,
    })
  })

  const meshRef = useRef<THREE.Mesh>(null!)
  const groupRef = useRef<THREE.Group>(null!)

  // Apply rotation if present
  useEffect(() => {
    if (rotation && groupRef.current) {
      const pivot = groupRef.current
      pivot.position.set(...rotation.origin)

      const angleRad = THREE.MathUtils.degToRad(rotation.angle)
      if (rotation.axis === "x") pivot.rotation.x = angleRad
      if (rotation.axis === "y") pivot.rotation.y = angleRad
      if (rotation.axis === "z") pivot.rotation.z = angleRad
    }
  }, [rotation])

  return (
    <group ref={groupRef} position={rotation ? rotation.origin : [0, 0, 0]}>
      <Box
        args={dimensions}
        ref={meshRef}
        position={
          rotation
            ? [position[0] - rotation.origin[0], position[1] - rotation.origin[1], position[2] - rotation.origin[2]]
            : position
        }
        material={materials}
      >
        { isSelected && <Edges lineWidth={3} color="red" /> }
      </Box>
    </group>
  )
}

export function processTexturePath(texturePath: string | undefined): string | undefined {
  if (!texturePath) return undefined
  let processedPath = texturePath

  // Handle texture variables like #all, #side, etc.
  if (processedPath.startsWith("#")) {
    // This function is for final path processing. Variable resolution should happen before.
    // If a variable like #stone reaches here, it implies it wasn't resolved.
    // We'll treat it as a literal name for now, potentially `block/stone`.
    processedPath = processedPath.substring(1)
    if (!processedPath.includes("/")) {
      // if just "stone"
      processedPath = `block/${processedPath}` // assume block
    }
  }

  processedPath = processedPath.replace(/.+:/, "")

  if (!/\.(png|jpe?g|gif|webp)$/i.test(processedPath)) {
    processedPath = `${processedPath}.png`
  }
  if (!processedPath.startsWith("http") && !processedPath.startsWith("/")) {
    processedPath = `/textures/${processedPath}`
  }
  return processedPath
}

export function SingleModelDisplay({ sceneDescription, isSelected, colorProps }: { sceneDescription: SceneDescription | null, isSelected?: boolean, colorProps?: MinecraftColorProperties }) {
  if (!sceneDescription || !sceneDescription.elements || sceneDescription.elements.length === 0) {
    return null // Don't render if no elements
  }
  return (
    <group>
      {sceneDescription.elements.map((element, index) => (
        <Suspense
          fallback={
            <Html center>
              <p>Loading element...</p>
            </Html>
          }
          key={index}
        >
          <MinecraftElement elementData={element} isSelected={isSelected} colorProps={colorProps} />
        </Suspense>
      ))}
    </group>
  )
}